import { FaroBulkDialog } from "@components/common/faro-table/bulk-dialog/bulk-dialog";
import { BulkSuccessDialog } from "@components/common/faro-table/bulk-dialog/bulk-success-dialog";
import { FaroTextButton } from "@components/common/faro-text-button";
import { useChangeProjectStatusBulkDialogContent } from "@components/table/projects/bulk-actions/bulk-change-project-status-helper";
import {
  BaseProjectsProps,
  ProjectArchivingState,
  SdbProject,
} from "@custom-types/project-types";
import { useHasValidFeatureProjectLevel } from "@hooks/feature-control/use-has-user-valid-feature-project-level";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { updateProjectDetails } from "@store/projects/projects-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import {
  bulkActionNameSelector,
  selectedEntitiesSelector,
  shouldShowSuccessDialogSelector,
} from "@store/table/table-selector";
import { setBulkActionName, updateOne } from "@store/table/table-slice";
import { RequiredFeatureProjectLevelName } from "@utils/feature-control/project/project-feature-control-types";
import { isFeatureEnabled } from "@utils/project-utils";
import { BulkActionEvents } from "@utils/track-event/track-event-list";
import { useEffect, useState } from "react";
import { useBulkActionsMode } from "@hooks/use-bulk-actions-mode";
import { getBulkActionButtonConfig } from "@components/common/faro-table/bulk-actions/bulk-actions-config";
import { Text, UnarchiveIcon, ArchiveIcon } from "@flat-ui";

interface Props extends BaseProjectsProps {
  /** The archiving status of the project */
  projectStatus: ProjectArchivingState;
}

/** Show change project status button and dispatch action where the bulk dialog will act accordingly */
export function BulkChangeProjectStatus({
  projectStatus,
  projects,
}: Props): JSX.Element {
  const dispatch = useAppDispatch();
  const [shouldShowDialog, setShouldShowDialog] = useState<boolean>(false);

  const bulkActionName = useAppSelector(bulkActionNameSelector);
  const shouldShowSuccessDialog = useAppSelector(
    shouldShowSuccessDialogSelector
  );
  const selectedEntities = useAppSelector(selectedEntitiesSelector("projects"));
  const { hasFeatureProjectLevel } = useHasValidFeatureProjectLevel();
  const { bulkDialogContent, successDialogContent } =
    useChangeProjectStatusBulkDialogContent(projectStatus);
  const mode = useBulkActionsMode();

  // Closes the dialog when the bulk action is successful to show the success dialog
  useEffect(() => {
    if (shouldShowSuccessDialog) {
      setShouldShowDialog(false);
    }
  }, [shouldShowSuccessDialog]);

  function onChangeProjectStatusClick(): void {
    dispatch(setBulkActionName("changeProjectStatus"));

    for (const entity of selectedEntities) {
      let isChangingProjectStatusAllowed = false;
      let hasUserValidRole = false;

      if (entity.features !== null) {
        let appropriateFeature: FaroCoreApiTypes.FeatureIdentifier =
          "project-unarchive";
        let appropriateRole =
          RequiredFeatureProjectLevelName.canUnarchiveProject;

        if (projectStatus === ProjectArchivingState.active) {
          appropriateFeature = "project-archive";
          appropriateRole = RequiredFeatureProjectLevelName.canArchiveProject;
        }

        isChangingProjectStatusAllowed = isFeatureEnabled(
          entity,
          appropriateFeature
        );

        hasUserValidRole = hasFeatureProjectLevel({
          roleName: appropriateRole,
          featuresAvailable: entity.featuresAvailable ?? [],
        });
      }

      if (!hasUserValidRole || !isChangingProjectStatusAllowed) {
        dispatch(
          updateOne({
            id: entity.id,
            changes: {
              status: "not-allowed",
              message: "You are not allowed to change status of this project",
            },
          })
        );
      }
    }

    setShouldShowDialog(true);
  }

  const isArchived = projectStatus === ProjectArchivingState.archived;
  const statusText = isArchived ? "Unarchive" : "Archive";
  const config = getBulkActionButtonConfig(mode, `${statusText} project`);

  return (
    <>
      {!!projects.length && (
        <FaroTextButton
          onClick={onChangeProjectStatusClick}
          tooltipText={config.tooltipText}
          rippleColor={config.rippleColor}
          sx={config.sx}
          startIcon={
            isArchived ? (
              <UnarchiveIcon sx={{ width: "18px", height: "18px" }} />
            ) : (
              <ArchiveIcon sx={{ width: "18px", height: "18px" }} />
            )
          }
        >
          {config.shouldShowText && <Text variant="buttonM">{statusText}</Text>}
        </FaroTextButton>
      )}

      {/* Checking for bulkActionName to make sure the dialog of another bulk action is not opened by mistake */}
      {bulkActionName === "changeProjectStatus" && shouldShowSuccessDialog && (
        <BulkSuccessDialog dialogContent={successDialogContent} />
      )}

      {bulkActionName === "changeProjectStatus" && shouldShowDialog && (
        <FaroBulkDialog
          dialogContent={bulkDialogContent}
          onClose={() => setShouldShowDialog(false)}
          trackingEvent={BulkActionEvents.bulkChangeProjectStatus}
          bulkActionCallback={(project: SdbProject) =>
            dispatch(
              updateProjectDetails({
                projectId: project.id,
                payload: {
                  archivingState:
                    projectStatus === ProjectArchivingState.active
                      ? "ARCHIVED"
                      : "UNARCHIVED",
                },
              })
            ).unwrap()
          }
          selectedEntities={selectedEntities}
          uniqueIdKey="id"
          tableDataType="projects"
        />
      )}
    </>
  );
}
